import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FullWidthGridColumn, GridRow } from 'components/StaticGrid'
import { useForm, Controller } from 'react-hook-form'
import PropTypes from 'prop-types'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTheme } from 'styled-components'
import { Tonality } from '@dsny/dsny-component-library'
import { Add } from '@dsny/dsny-component-library/icons'
import { stepThreeSchema } from './schema'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import {
  Container,
  FormWrapper,
  FullWidthButton
} from '../../components/AuthForm.styles'
import SelectInput from 'components/SelectInput'
import { Error, SignupHalfColumn, StyledTextField } from './Steps.styles'
import { COUNTRY_LIST } from 'constants/countryList'
import { US_PROVINCES, CA_PROVINCES } from '../../../../constants/provinceList'
import {
  ReportSource,
  ReportFormat,
  OccupationOptions,
  INDUSTRY_OPTIONS
} from 'constants/enums'

const StepThree = ({ onSubmit, signUpData }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMobileMediaQuery()
  const [provinces, setProvinces] = useState([])
  const [isProvinceRequired, setIsProvinceRequired] = useState(false)
  const [shouldShowWebsite, setShouldShowWebsite] = useState(false)
  const [additionalFieldsCount, setAdditionalFieldsCount] = useState(0)
  const context = { industry: signUpData?.industry }

  const {
    control,
    setValue,
    handleSubmit,
    register,
    trigger,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(stepThreeSchema, { context })
  })

  const updateProvinceField = (value) => {
    const isCanada = value === 'CA'
    const isUS = value === 'US'
    setIsProvinceRequired(isCanada || isUS)
    if (isUS) {
      setProvinces(
        US_PROVINCES.map((province) => {
          return { value: province.id, label: province.description }
        })
      )
    } else if (isCanada) {
      setProvinces(
        CA_PROVINCES.map((province) => {
          return { value: province.id, label: province.description }
        })
      )
    } else {
      setProvinces([])
    }
  }

  const onChangeCountry = (value) => {
    setValue('country', value)
    trigger('country')
    updateProvinceField(value)
  }

  const onChangeProvince = (value) => {
    setValue('state', value)
    trigger('state')
  }

  const onChangeReporting = (value) => {
    setValue('reporting', value)
    trigger('reporting')
  }

  const onChangePrimaryFormat = (value) => {
    setValue('primary_format', value)
    trigger('primary_format')
  }

  const onChangeMediaType = (value) => {
    setValue('media_type', value)
    trigger('media_type')
  }

  const addAdditionalField = () => {
    if (additionalFieldsCount < 2) {
      setAdditionalFieldsCount((prev) => prev + 1)
    }
  }

  const getFormatFieldName = (index) => {
    return index === 0 ? 'secondary_format' : 'additional_format'
  }

  const getFormatFieldLabel = (index) => {
    return index === 0
      ? t('signup.secondary-format')
      : t('signup.additional-format')
  }

  const onChangeAdditionalField = (fieldName, value) => {
    setValue(fieldName, value)
    trigger(fieldName)
  }

  const countries = useMemo(
    () =>
      COUNTRY_LIST.map((el) => {
        return { value: el.id, label: el.description }
      }),
    []
  )

  const reportSources = useMemo(() => {
    return Object.values(ReportSource).map((el) => {
      return { value: el.value, label: t(el.display) }
    })
  }, [])

  const formats = useMemo(() => {
    return Object.values(ReportFormat).map((el) => {
      return { value: el.value, label: t(el.display) }
    })
  }, [])

  const mediaTypes = useMemo(() => {
    return Object.values(OccupationOptions).map((el) => {
      return { value: el.value, label: t(el.display) }
    })
  }, [])

  return (
    <Container style={{ padding: 0, alignItems: 'flex-start' }}>
      <Tonality.UIHeading
        headingStyle="headline4"
        color={theme.colors.neutralW100}
        style={{ marginBottom: 12 }}
      >
        {t('signup.about-your-company')}
      </Tonality.UIHeading>

      <Tonality.UIText
        textStyle="body"
        color={theme.colors.neutralW70}
        style={{ fontFamily: 16, marginBottom: 32 }}
      >
        {t('signup.about-company-description')}
      </Tonality.UIText>

      <FormWrapper>
        <form
          data-cy="signup-step-three-form"
          onSubmit={handleSubmit((values, e) => {
            onSubmit(values, e)
          })}
        >
          <GridRow gutterWidth={16}>
            <FullWidthGridColumn>
              <div data-cy="signup-group-owner">
                <StyledTextField
                  required
                  id="group_owner"
                  name="group_owner"
                  {...register('group_owner')}
                  ref={null}
                  inputRef={register('group_owner').ref}
                  labelText={t('signup.organization-group-owner')}
                  error={!!errors.group_owner && t('signup.group-owner-error')}
                />
              </div>

              {!shouldShowWebsite && (
                <Tonality.UIText
                  textStyle="body"
                  onClick={() => setShouldShowWebsite(!shouldShowWebsite)}
                  color={theme.colors.player50}
                  style={{
                    display: 'flex',
                    fontSize: 14,
                    marginBottom: 24,
                    marginTop: 8,
                    cursor: 'pointer'
                  }}
                >
                  <Add width={16} style={{ marginRight: 4 }} />
                  {t('signup.add-website')}
                </Tonality.UIText>
              )}
            </FullWidthGridColumn>
          </GridRow>

          {shouldShowWebsite && (
            <GridRow
              gutterWidth={16}
              style={{ marginBottom: 24, marginTop: 24 }}
            >
              <FullWidthGridColumn>
                <div data-cy="signup-website">
                  <StyledTextField
                    required
                    id="web_site"
                    name="web_site"
                    {...register('web_site')}
                    ref={null}
                    inputRef={register('web_site').ref}
                    labelText={`${t('signup.website')} (${t('signup.optional')})`}
                    error={errors.web_site?.message}
                  />
                </div>
              </FullWidthGridColumn>
            </GridRow>
          )}

          <GridRow gutterWidth={16} style={{ marginBottom: 24 }}>
            <FullWidthGridColumn>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    dataCy="signup-country"
                    required
                    label={t('signup.country')}
                    selectOptions={countries}
                    value={field.value}
                    onChangeValue={(val) => {
                      field.onChange(val)
                      onChangeCountry(val)
                    }}
                    error={!!errors.country}
                  />
                )}
              />
              {errors.country && <Error>{t('signup.country-error')}</Error>}
            </FullWidthGridColumn>
          </GridRow>

          <GridRow gutterWidth={16} style={{ marginBottom: 24 }}>
            <SignupHalfColumn style={{ marginBottom: isMobile ? 24 : 0 }}>
              {isProvinceRequired ? (
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      dataCy="signup-state"
                      required={isProvinceRequired}
                      label={t('signup.state-province')}
                      selectOptions={provinces}
                      value={field.value}
                      onChangeValue={(val) => {
                        field.onChange(val)
                        onChangeProvince(val)
                      }}
                      error={!!errors.state}
                    />
                  )}
                />
              ) : (
                <div data-cy="signup-state">
                  <StyledTextField
                    id="state"
                    name="state"
                    {...register('state')}
                    ref={null}
                    inputRef={register('state').ref}
                    labelText={t('signup.state-province')}
                    error={errors.state?.message}
                  />
                </div>
              )}
              {errors.state && <Error>{t('signup.state-error')}</Error>}
            </SignupHalfColumn>

            <SignupHalfColumn>
              <div data-cy="signup-city">
                <StyledTextField
                  required
                  id="city"
                  name="city"
                  {...register('city')}
                  ref={null}
                  inputRef={register('city').ref}
                  labelText={t('signup.city')}
                  error={!!errors.city && t('signup.city-error')}
                />
              </div>
            </SignupHalfColumn>
          </GridRow>

          {signUpData?.industry === INDUSTRY_OPTIONS.RADIO && (
            <>
              <GridRow gutterWidth={16} style={{ paddingBottom: 16 }}>
                <SignupHalfColumn style={{ marginBottom: isMobile ? 24 : 0 }}>
                  <Controller
                    name="reporting"
                    control={control}
                    render={({ field }) => (
                      <SelectInput
                        dataCy="signup-reporting"
                        required
                        label={t('signup.reporting')}
                        selectOptions={reportSources}
                        value={field.value}
                        onChangeValue={(val) => {
                          field.onChange(val)
                          onChangeReporting?.(val)
                        }}
                        error={!!errors.reporting}
                      />
                    )}
                  />
                  {errors.reporting && (
                    <Error>{t('signup.reporting-error')}</Error>
                  )}
                </SignupHalfColumn>

                <SignupHalfColumn>
                  <div data-cy="signup-call-letter">
                    <StyledTextField
                      required
                      id="call_letter"
                      name="call_letter"
                      {...register('call_letter')}
                      ref={null}
                      inputRef={register('call_letter').ref}
                      labelText={t('signup.call-letter')}
                      error={
                        !!errors.call_letter && t('signup.call-letter-error')
                      }
                    />
                  </div>
                </SignupHalfColumn>
              </GridRow>
            </>
          )}

          {signUpData?.industry === INDUSTRY_OPTIONS.PRESS_PUBLICITY_MEDIA && (
            <GridRow gutterWidth={16} style={{ marginBottom: 24 }}>
              <FullWidthGridColumn>
                <Controller
                  name="media_type"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      dataCy="signup-media-type"
                      label={t('signup.media-type')}
                      selectOptions={mediaTypes}
                      value={field.value}
                      onChangeValue={(val) => {
                        field.onChange(val)
                        onChangeMediaType?.(val)
                      }}
                      error={!!errors.media_type}
                    />
                  )}
                />
                {errors.media_type && (
                  <Error>{t('signup.media-type-error')}</Error>
                )}
              </FullWidthGridColumn>
            </GridRow>
          )}

          <input
            id="industry"
            name="industry"
            type="hidden"
            value={signUpData?.industry}
            ref={null}
            inputRef={register('industry').ref}
            {...register('industry')}
          />

          <GridRow gutterWidth={16} style={{ marginBottom: 24 }}>
            <FullWidthGridColumn>
              <Controller
                name="primary_format"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    dataCy="signup-primary-format"
                    required
                    label={t('signup.primary-format')}
                    selectOptions={formats}
                    value={field.value}
                    onChangeValue={(val) => {
                      field.onChange(val)
                      onChangePrimaryFormat?.(val)
                    }}
                    error={!!errors.primary_format}
                  />
                )}
              />
              {errors.primary_format && (
                <Error>{t('signup.primary-format-error')}</Error>
              )}
            </FullWidthGridColumn>
          </GridRow>

          {[...Array(additionalFieldsCount)].map((_, index) => {
            const fieldName = getFormatFieldName(index)
            return (
              <GridRow
                key={fieldName}
                gutterWidth={16}
                style={{ marginBottom: 24 }}
              >
                <FullWidthGridColumn>
                  <Controller
                    name={fieldName}
                    control={control}
                    render={({ field }) => (
                      <SelectInput
                        dataCy={`signup-additional-format-${index + 1}`}
                        label={getFormatFieldLabel(index)}
                        selectOptions={formats}
                        value={field.value}
                        onChangeValue={(val) => {
                          field.onChange(val)
                          onChangeAdditionalField?.(fieldName, val)
                        }}
                        error={!!errors[fieldName]}
                      />
                    )}
                  />
                  {errors[fieldName] && (
                    <Error>{errors[fieldName]?.message}</Error>
                  )}
                </FullWidthGridColumn>
              </GridRow>
            )
          })}

          {additionalFieldsCount < 2 && (
            <GridRow gutterWidth={16}>
              <FullWidthGridColumn>
                <Tonality.UIText
                  textStyle="body"
                  color={theme.colors.player50}
                  style={{ display: 'flex', fontSize: 14, cursor: 'pointer' }}
                  onClick={addAdditionalField}
                >
                  <Add width={16} style={{ marginRight: 4 }} />
                  {t('signup.add-additional-format')}
                </Tonality.UIText>
              </FullWidthGridColumn>
            </GridRow>
          )}

          <FullWidthButton
            data-cy="signup-step-three-submit"
            type="submit"
            color="primary"
            setting="player"
            style={{ marginTop: 32 }}
          >
            {t('signup.create-account')}
          </FullWidthButton>
        </form>
      </FormWrapper>
    </Container>
  )
}

StepThree.propTypes = {
  onSubmit: PropTypes.func.isRequired
}

export default StepThree
